import { makeStyles } from '@mui/styles';
import Grid from 'apollo-react/components/Grid';
import Modal from 'apollo-react/components/Modal';
import Tag from 'apollo-react/components/Tag';
import Typography from 'apollo-react/components/Typography';
import React, { useContext, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import AddedDocumentsTable from './AddedDocumentsTable';
import DocumentTree from './DocumentTree';
import {
  fetchDocumentRepositories,
  fetchDocumentRepositoryDetails,
} from '../../../actions/DocumentRepositoryActions';
import { addError } from '../../../actions/ErrorActions';
import DocumentSearchTable from '../../../components/DocumentSearchTable';
import EnhancedSearchDropdown from '../../../components/EnhancedSearchDropdown';
import { useFormSelector } from '../../../custom-hooks';
import { useTranslation } from '../../../custom-hooks/useTranslation';
import { getRepositoryInfo } from '../../../selectors/DocumentRepositorySelectors';
import { mapRepositoryDetails } from '../../../utils/commonUtils';
import { isEmpty } from '../../../utils/ramdaUtils';
import { bulkAddingNewRows } from '../../actions/runtime.actions';
import { FormContext } from '../../context';
import { getEntityDefinition } from '../../selectors/common.selectors';
import { getValueWithDataType } from '../../utils/runtime.utils';

const useStyles = makeStyles({
  modal: {
    width: '100%',
    maxWidth: '85%',
    '@media (max-width: 1200px)': {
      maxWidth: '100%',
    },
  },
  repoTitle: {
    fontSize: 16,
  },
  leftPanel: {
    paddingRight: 0,
    marginBottom: 24,
    '@media (min-width: 900px)': {
      paddingRight: 12,
      marginBottom: 0,
    },
  },
  rightPanel: {
    paddingLeft: 0,
    '@media (min-width: 900px)': {
      paddingLeft: 12,
    },
  },
  docSearchTable: {
    marginBottom: 24,
  },
});

const REFERENCED_COLUMNS = {
  'V_SS_DOC_SHARE_REVIEW-V_SS_DOC_SHARE_AFF_DOCS.DSR_DOCUMENT_NO': 'DOC_DOCUMENT_NO',
  'V_SS_DOC_SHARE_REVIEW-V_SS_DOC_SHARE_AFF_DOCS.DSR_DOCUMENT_REV': 'DOC_DOCUMENT_REV',
  'V_SS_DOC_SHARE_REVIEW-V_SS_DOC_SHARE_AFF_DOCS.DSR_DOC_EFFECTIVE_DATE': 'DOC_EFFECTIVE_DATE',
  'V_SS_DOC_SHARE_REVIEW-V_SS_DOC_SHARE_AFF_DOCS.DSR_DOC_TITLE': 'DOC_TITLE',
  'V_SS_DOC_SHARE_REVIEW-V_SS_DOC_SHARE_AFF_DOCS.DSR_DOC_TYPE': 'DOT_TYPE',
  'V_SS_DOC_SHARE_REVIEW-V_SS_DOC_SHARE_AFF_DOCS.DSR_DOCUMENT_ID': 'DOC_ID',
};

const FILTERED_OUT_REPOS = ['Expired', 'Inworks'];

const FILTER_ONLY_CURRENT = {
  openGroup: true,
  fieldName: 'DOC_DSS_NAME',
  sqlOperator: 'CT',
  value: 'CURRENT',
  relationalOperator: 'AND',
  closeGroup: true,
};

const AddDocumentModal = ({
  open,
  onClose,
  entityName,
  index,
  highestPrintOrderNubmer,
  existingRows,
}) => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const translateString = useTranslation();
  const formKey = useContext(FormContext);

  const { documentRepositories, documentRepositoryDetails, defaultRepository } = useSelector(
    getRepositoryInfo
  );
  const entityDefinition = useFormSelector(getEntityDefinition);

  const [repositoryName, setRepositoryName] = useState('');
  const [selectedRepositoryItem, setSelectedRepositoryItem] = useState({});
  const [selectedDocuments, setSelectedDocuments] = useState([]);
  const [duplicatedDocuments, setDuplicatedDocuments] = useState([]);

  const repositoryItems = useMemo(() => {
    const nodes = documentRepositoryDetails.ChildNodes || [];
    return nodes.reduce(mapRepositoryDetails, {});
  }, [documentRepositoryDetails]);

  const handleRepositoryName = (repoName) => {
    setRepositoryName(repoName);
    setSelectedRepositoryItem({});
  };

  useEffect(() => {
    dispatch(fetchDocumentRepositories());
  }, [dispatch]);

  useEffect(() => {
    if (repositoryName) {
      dispatch(fetchDocumentRepositoryDetails({ repositoryName }));
    }
  }, [repositoryName, dispatch]);

  useEffect(() => {
    if (defaultRepository) {
      handleRepositoryName(defaultRepository);
    }
  }, [defaultRepository]);

  useEffect(() => {
    if (open) {
      handleRepositoryName(defaultRepository);
      setSelectedDocuments([]);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const handleSave = () => {
    const rows = selectedDocuments.map((doc, i) => ({
      DBP_DOCUMENT_NO: doc.DOC_DOCUMENT_NO,
      DBP_DOCUMENT_REV: doc.DOC_DOCUMENT_REV,
      DBP_DOC_TITLE: doc.DOC_TITLE,
      DBP_DOC_TYPE: doc.DOT_TYPE,
      DBP_DOC_EFFECTIVE_DATE: doc.DOC_EFFECTIVE_DATE,
      DBP_DOCUMENT_ID: doc.DOC_ID,
      DBP_PRINT_ORDER: highestPrintOrderNubmer + i + 1,
    }));

    const entityType = Object.keys(entityDefinition)[0];

    const rowsWithCastedValues = rows.map((row) =>
      Object.keys(row).reduce(
        (acc, key) => ({
          ...acc,
          [key]: getValueWithDataType(row[key], entityDefinition, entityType, entityName, key),
        }),
        {}
      )
    );

    const duplicates = existingRows.reduce((prev, curr) => {
      if (rowsWithCastedValues.some((row) => row.DBP_DOCUMENT_ID === curr.DBP_DOCUMENT_ID)) {
        return [...prev, curr];
      } else {
        return prev;
      }
    }, []);

    if (duplicates.length) {
      dispatch(
        addError({
          message: 'Duplicate documents detected. Please remove them from your list.',
          isTopBanner: true,
        })
      );
      setDuplicatedDocuments(duplicates);
      return;
    }

    if (rowsWithCastedValues.length) {
      dispatch(bulkAddingNewRows({ entityName, index, newRows: rowsWithCastedValues, formKey }));
    }

    setDuplicatedDocuments([]);
    onClose();
  };

  const filteredRepos = useMemo(
    () =>
      documentRepositories.reduce((acc, repo) => {
        if (!FILTERED_OUT_REPOS.includes(repo.Name)) {
          return [
            ...acc,
            {
              translatedSearchName: translateString(repo.Name),
              id: repo.Id,
              searchName: repo.Name,
            },
          ];
        }

        return acc;
      }, []),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [documentRepositories]
  );

  return (
    <Modal
      className={classes.modal}
      open={open}
      onClose={onClose}
      title={translateString('Add Document')}
      buttonProps={[
        { label: translateString('Cancel') },
        {
          label: translateString('Save'),
          onClick: handleSave,
        },
      ]}
      disableBackdropClick
      disableEscapeKeyDown
    >
      <Grid container>
        <Grid className={classes.leftPanel} item xs={12} md={3}>
          <Typography className={classes.repoTitle} variant="h3" gutterBottom>
            {translateString('Document Repository')}
          </Typography>
          {repositoryName && (
            <Tag
              label={`${translateString(repositoryName)} ${
                selectedRepositoryItem.NodeText
                  ? ` / ${translateString(selectedRepositoryItem.NodeText)}`
                  : ''
              }`}
              variant="fuchsia"
            />
          )}
          {filteredRepos.length ? (
            <EnhancedSearchDropdown
              storeKey="DOCUMENT_REPOSITORY"
              selectOptions={filteredRepos}
              selectedValue={repositoryName}
              onChange={(value) => handleRepositoryName(value)}
              showPinned
              showRecent
              fullWidth
            />
          ) : (
            <Typography>{`${translateString('Loading')}...`}</Typography>
          )}
          {!isEmpty(documentRepositoryDetails) && (
            <DocumentTree
              repositoryName={repositoryName}
              documentRepositoryDetails={documentRepositoryDetails}
              repositoryItems={repositoryItems}
              onSelecteItem={(item) => setSelectedRepositoryItem(item)}
            />
          )}
        </Grid>
        <Grid className={classes.rightPanel} item xs={12} md={9}>
          {selectedRepositoryItem.queryName && (
            <>
              <div className={classes.docSearchTable}>
                <DocumentSearchTable
                  selectedRepositoryItem={selectedRepositoryItem}
                  selectedDocuments={selectedDocuments}
                  setSelectedDocuments={setSelectedDocuments}
                  referencedColumns={REFERENCED_COLUMNS}
                  initialRowsPerPage={5}
                  customFilter={[FILTER_ONLY_CURRENT]}
                  addDocumentModal
                />
              </div>
              <AddedDocumentsTable
                selectedDocuments={selectedDocuments}
                setSelectedDocuments={setSelectedDocuments}
                duplicatedDocuments={duplicatedDocuments}
              />
            </>
          )}
        </Grid>
      </Grid>
    </Modal>
  );
};

export default AddDocumentModal;
